import { useState, useId, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileUploader } from "@/components/common/FileUploader";
import type { UploadedFileItem } from "@/components/common/FileUploader";
import { useLingui } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import {
  PlusCircle,
  Loader2,
  X,
  Mail,
  Phone,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AddressAutocompleteFields } from "@/components/address/AddressAutocompleteFields";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OrganizationSelect } from "@/components/common/entity-select/OrganizationSelect";
import { dataApi } from "@/lib/api/admin-api";
import type { Company } from "@/types/company";
import { useAuth } from "@/hooks/useAuth";
import { canAccessAction } from "@/lib/access-control/ui-access";
import {
  ACTIVE_ADDRESS_COUNTRY,
  isPostalCodeValid,
} from "@/lib/config/address";

// ─── Schema ───────────────────────────────────────────────────────────────────

const companyFormSchema = z.object({
  name: z.string().min(1, t`Concept name is required`),
  slug: z
    .string()
    .min(1, t`Slug is required`)
    .regex(/^[a-z0-9-]+$/, t`Slug may only contain lowercase letters, numbers, and hyphens`),
  description: z.string().optional(),
  email: z
    .union([z.literal(""), z.string().email(t`Enter a valid email address`)])
    .optional(),
  phone: z.string().optional(),
  isActive: z.boolean().optional(),
  // Create-only address fields
  addressLine1: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
}).superRefine((value, ctx) => {
  if (value.postalCode && !isPostalCodeValid(value.country ?? ACTIVE_ADDRESS_COUNTRY, value.postalCode)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t`Enter a valid ZIP code`,
      path: ["postalCode"],
    });
  }
});

type CompanyFormValues = z.infer<typeof companyFormSchema>;

// ─── Layout helpers ───────────────────────────────────────────────────────────

function FormSection({
  title,
  description,
  children,
  noBorder,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  noBorder?: boolean;
}) {
  return (
    <div className={`flex ${noBorder ? "" : "border-b border-(--color-border)"}`}>
      <div className="w-[300px] shrink-0 bg-(--color-surface-muted) border-r border-(--color-border) p-5 flex flex-col gap-2">
        <h3 className="text-sm font-medium text-(--color-text-primary)">{title}</h3>
        {description && (
          <p className="text-sm text-(--color-text-secondary) leading-snug">{description}</p>
        )}
      </div>
      <div className="flex-1 p-5 flex flex-col gap-4">{children}</div>
    </div>
  );
}

function FormField({
  label,
  id,
  error,
  children,
}: {
  label: string;
  id?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 flex-1">
      <Label htmlFor={id} className="text-sm font-medium text-(--color-text-primary)">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-(--color-danger-text)">{error}</p>}
    </div>
  );
}

const inputClass = "";
const selectTriggerClass = "";

// ─── CompanyFormDialog ────────────────────────────────────────────────────────
// Pure controlled dialog. No trigger button — callers manage open state.

interface CompanyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: Company;
}

export function CompanyFormDialog({
  open,
  onOpenChange,
  company,
}: CompanyFormDialogProps) {
  const { t } = useLingui();
  const { user, hasPermission, hasRole } = useAuth();
  const queryClient = useQueryClient();
  const idPrefix = useId();
  const isEditMode = !!company;

  const accessInput = { user, hasPermission, hasRole };
  const createAction = {
    permissionsAny: ["company:create"],
  };
  const canCreateCompany = canAccessAction(accessInput, createAction);
  const userOrgId = user?.organization?.id;
  const isInternalOrgSelectorRequired = !isEditMode && (hasRole("SUPER_ADMIN") || hasRole("SUPPORT"));
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(
    isInternalOrgSelectorRequired ? null : (userOrgId ?? null),
  );
  const [selectedOrganizationName, setSelectedOrganizationName] = useState("");

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadedLogos, setUploadedLogos] = useState<UploadedFileItem[]>([]);
  const [isExistingLogoDetached, setIsExistingLogoDetached] = useState(false);
  const slugManuallyEdited = useRef(isEditMode);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      slug: "",
      description: "",
      email: "",
      phone: "",
      isActive: undefined,
      addressLine1: "",
      city: "",
      state: "",
      postalCode: "",
      country: ACTIVE_ADDRESS_COUNTRY,
      latitude: undefined,
      longitude: undefined,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: company?.name ?? "",
        slug: company?.slug ?? "",
        description: company?.description ?? "",
        email: company?.email ?? "",
        phone: company?.phone ?? "",
        isActive: company?.isActive,
        addressLine1: "",
        city: "",
        state: "",
        postalCode: "",
        country: ACTIVE_ADDRESS_COUNTRY,
        latitude: undefined,
        longitude: undefined,
      });
      slugManuallyEdited.current = isEditMode;
      setUploadedLogos([]);
      setIsExistingLogoDetached(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company?.id, isEditMode, isInternalOrgSelectorRequired, userOrgId]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setValue("name", name, { shouldValidate: true });
    if (!slugManuallyEdited.current) {
      const slug = name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-{2,}/g, "-")
        .replace(/^-|-$/g, "");
      setValue("slug", slug, { shouldValidate: true });
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    slugManuallyEdited.current = true;
    setValue("slug", e.target.value, { shouldValidate: true });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
      slugManuallyEdited.current = isEditMode;
      setSubmitError(null);
      setUploadedLogos([]);
      setIsExistingLogoDetached(false);
      setSelectedOrganizationId(isInternalOrgSelectorRequired ? null : (userOrgId ?? null));
      setSelectedOrganizationName("");
    }
    onOpenChange(nextOpen);
  };

  /**
   * `undefined` leaves a stored logo alone and `null` clears it — the API skips
   * any field arriving as `undefined`, so detaching has to be explicit (the
   * same wire contract as material photos, T-506).
   */
  const uploadedLogoUrl = uploadedLogos[0]?.imageUrl;
  const resolvedLogoUrl =
    uploadedLogoUrl ?? (isEditMode && isExistingLogoDetached ? null : undefined);

  const createMutation = useMutation({
    mutationFn: (data: CompanyFormValues) => {
      const targetOrganizationId = isInternalOrgSelectorRequired
        ? selectedOrganizationId
        : (userOrgId ?? null);

      if (!targetOrganizationId) {
        throw new Error(t`Please select an organization`);
      }

      return dataApi.createCompany({
        organizationId: targetOrganizationId,
        name: data.name.trim(),
        slug: data.slug,
        description: data.description?.trim() || undefined,
        logoUrl: uploadedLogoUrl,
        email: data.email?.trim() || undefined,
        phone: data.phone?.trim() || undefined,
        addressLine1: data.addressLine1?.trim() || undefined,
        city: data.city?.trim() || undefined,
        state: data.state?.trim() || undefined,
        postalCode: data.postalCode?.trim() || undefined,
        country: ACTIVE_ADDRESS_COUNTRY,
        latitude: data.latitude,
        longitude: data.longitude,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      handleOpenChange(false);
      toast.success(t`Concept created successfully`);
    },
    onError: (error: Error) => {
      setSubmitError(error.message || t`Failed to create concept`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CompanyFormValues) =>
      dataApi.updateCompany(company!.id, {
        name: data.name.trim(),
        slug: data.slug,
        description: data.description?.trim() || undefined,
        logoUrl: resolvedLogoUrl,
        email: data.email?.trim() || undefined,
        phone: data.phone?.trim() || undefined,
        isActive: data.isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      handleOpenChange(false);
      toast.success(t`Concept updated successfully`);
    },
    onError: (error: Error) => {
      setSubmitError(error.message || t`Failed to update concept`);
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Matches how the API names the location it provisions (`${name} HQ`).
  const conceptName = watch("name")?.trim();
  const headquartersLocationName = conceptName ? `"${conceptName} HQ"` : t`"<concept name> HQ"`;

  const onSubmit = (data: CompanyFormValues) => {
    setSubmitError(null);

    if (!isEditMode && !canCreateCompany) {
      setSubmitError(t`Access Restricted`);
      return;
    }

    if (!isEditMode && isInternalOrgSelectorRequired && !selectedOrganizationId) {
      setSubmitError(t`Please select an organization`);
      return;
    }

    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[900px] p-0 overflow-hidden gap-0 border-none rounded-(--radius-lg)"
      >
        <div className="flex flex-col bg-(--color-surface-content) max-h-[90vh]">
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-(--color-border)">
            <div className="flex flex-col gap-1.5">
              <DialogTitle className="text-2xl font-medium text-(--color-text-primary) leading-none">
                {isEditMode ? t`Edit Concept` : t`Create Concept`}
              </DialogTitle>
              <DialogDescription className="text-base text-(--color-text-secondary) font-normal leading-none">
                {isEditMode
                  ? t`Update the concept information below.`
                  : t`Create a new customer concept to access Yes, Chef and configure locations, users, and content.`}
              </DialogDescription>
            </div>
            <DialogClose
              aria-label={t`Close dialog`}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
            >
              <X className="h-6 w-6" />
            </DialogClose>
          </div>

          <form
            id={`${idPrefix}-form`}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col flex-1 overflow-y-auto"
          >
            {/* Company Details */}
            <FormSection
              title={t`Concept details`}
              description={t`Basic information about the concept.`}
              noBorder={isEditMode}
            >
              {!isEditMode && isInternalOrgSelectorRequired && (
                <div className="flex gap-3">
                  <FormField
                    label={t`Organization`}
                    error={!selectedOrganizationId ? t`Please select an organization` : undefined}
                  >
                    <OrganizationSelect
                      value={selectedOrganizationId}
                      adminScope={true}
                      displayValue={selectedOrganizationName || undefined}
                      onChange={(orgId, organization) => {
                        setSelectedOrganizationId(orgId);
                        setSelectedOrganizationName(organization.name);
                        setSubmitError(null);
                      }}
                      onClear={() => {
                        setSelectedOrganizationId(null);
                        setSelectedOrganizationName("");
                      }}
                      triggerClassName={selectTriggerClass}
                    />
                  </FormField>
                </div>
              )}

              <div className="flex gap-3">
                <FormField
                  label={t`Concept name`}
                  id={`${idPrefix}-name`}
                  error={errors.name?.message}
                >
                  <Input
                    id={`${idPrefix}-name`}
                    placeholder={t`Enter concept name...`}
                    {...register("name")}
                    onChange={handleNameChange}
                    className={`${inputClass} ${errors.name ? "border-(--color-danger)" : ""}`}
                    autoFocus
                  />
                </FormField>
                <FormField
                  label={t`Slug`}
                  id={`${idPrefix}-slug`}
                  error={errors.slug?.message}
                >
                  <Input
                    id={`${idPrefix}-slug`}
                    placeholder={t`concept-slug`}
                    {...register("slug")}
                    onChange={handleSlugChange}
                    className={`${inputClass} ${errors.slug ? "border-(--color-danger)" : ""}`}
                  />
                </FormField>
              </div>

              <div className="flex gap-3">
                <FormField
                  label={t`Concept email`}
                  id={`${idPrefix}-email`}
                  error={errors.email?.message}
                >
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-(--color-text-secondary)" />
                    <Input
                      id={`${idPrefix}-email`}
                      type="email"
                      placeholder={t`Enter concept email...`}
                      {...register("email")}
                      className={`${inputClass} pl-11 ${errors.email ? "border-(--color-danger)" : ""}`}
                    />
                  </div>
                </FormField>
                <FormField label={t`Phone`} id={`${idPrefix}-phone`}>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-(--color-text-secondary)" />
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id={`${idPrefix}-phone`}
                          type="tel"
                          placeholder={t`Enter phone...`}
                          value={field.value ?? ""}
                          onChange={(event) => {
                            const normalized = event.target.value.replace(/\D/g, "");
                            field.onChange(normalized);
                          }}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className={`${inputClass} pl-11`}
                        />
                      )}
                    />
                  </div>
                </FormField>
              </div>

              <div className="flex gap-3">
                <FormField label={t`Logo`}>
                  <FileUploader
                    // Remount on open, and on a change of target, so a previous
                    // session's pending upload cannot carry into the next one.
                    key={`${open ? "open" : "closed"}-${company?.id ?? "new"}`}
                    module="company"
                    label={undefined}
                    optional
                    multiple={false}
                    maxFiles={1}
                    disabled={isPending}
                    onUploadedItemsChange={setUploadedLogos}
                    existingImage={
                      company?.logoUrl
                        ? { imageUrl: company.logoUrl, label: t`Current logo` }
                        : undefined
                    }
                    onExistingImageDetachedChange={setIsExistingLogoDetached}
                  />
                </FormField>
                {isEditMode && (
                  <FormField label={t`Status`}>
                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value === false ? "inactive" : "active"}
                          onValueChange={(v) => field.onChange(v === "active")}
                        >
                          <SelectTrigger className={selectTriggerClass}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">{t`Active`}</SelectItem>
                            <SelectItem value="inactive">{t`Inactive`}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </FormField>
                )}
              </div>

              <FormField label={t`Description`} id={`${idPrefix}-description`}>
                <Textarea
                  id={`${idPrefix}-description`}
                  placeholder={t`Enter a description...`}
                  {...register("description")}
                  className="rounded-lg border-(--color-border) bg-(--color-surface-content) shadow-(--shadow-button) placeholder:text-(--color-text-secondary) text-sm min-h-[80px] resize-none"
                />
              </FormField>
            </FormSection>

            {/* Address — create mode only */}
            {!isEditMode && (
              <FormSection
                title={t`Concept address`}
                description={t`Creates a headquarters location linked to this concept.`}
                noBorder
              >
                {/*
                  The side-panel description above is easy to scroll past, and the
                  HQ location then turns up in the location list looking like
                  something nobody added. Name it here, where the address is being
                  typed, so the row is recognizable later.
                */}
                <p className="rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 py-2 text-sm text-(--color-text-secondary)">
                  {t`Adding an address creates one location for this concept, named ${headquartersLocationName} and coded HQ. Leave the address blank to add locations yourself.`}
                </p>
                <AddressAutocompleteFields<CompanyFormValues>
                  idPrefix={idPrefix}
                  control={control}
                  register={register}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  errors={errors}
                  inputClassName={inputClass}
                  selectTriggerClassName={selectTriggerClass}
                  labels={{ postalCode: t`ZIP Code` }}
                  placeholders={{ address: t`Street address...` }}
                />
              </FormSection>
            )}
          </form>

          {/* Footer */}
          <div className="p-5 border-t border-(--color-border) flex flex-col gap-3 bg-(--color-surface-content) shrink-0">
            {submitError && (
              <div className="flex items-center gap-2 rounded-lg bg-(--color-danger)/10 border border-(--color-danger)/20 px-4 py-2.5 text-sm text-(--color-danger-text)">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <p className="text-sm text-(--color-text-secondary)">
                {isEditMode
                  ? t`Changes will be applied immediately`
                  : t`Status defaults to Active • Concept ID auto-generated`}
              </p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  className="w-[200px] h-[52px] rounded-(--radius-food-thumb) border-(--color-border) text-base font-medium text-(--color-text-primary)"
                >
                  {t`Cancel`}
                </Button>
                <Button
                  form={`${idPrefix}-form`}
                  type="submit"
                  disabled={isPending || (!isEditMode && isInternalOrgSelectorRequired && !selectedOrganizationId)}
                  className="w-[200px] h-[52px] rounded-(--radius-food-thumb) bg-(--color-action) text-(--color-action-fg) text-base font-medium hover:bg-(--color-action)/90 disabled:opacity-50"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEditMode ? t`Save Changes` : t`Create Concept`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── CreateCompanyDialog ──────────────────────────────────────────────────────
// Self-contained trigger button + dialog for the create flow.

export function CreateCompanyDialog() {
  const { t } = useLingui();
  const { user, hasPermission, hasRole } = useAuth();
  const [open, setOpen] = useState(false);

  const canCreateCompany = canAccessAction(
    { user, hasPermission, hasRole },
    {
      permissionsAny: ["company:create"],
    },
  );

  return (
    <>
      <Button className="gap-2" onClick={() => setOpen(true)} disabled={!canCreateCompany}>
        <PlusCircle className="h-4 w-4" />
        {t`Create New Concept`}
      </Button>
      <CompanyFormDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
